import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Users } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardHeader } from '@/components/ui/Card';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/states/EmptyState';
import { ErrorState } from '@/components/states/ErrorState';
import { listCustomers } from '@/api/endpoints/admin';
import { queryKeys } from '@/api/queryKeys';
import { titleCase } from '@/lib/format';

/**
 * The support directory. Somebody calls; you have their name or their email and
 * nothing else, so those are what this searches.
 */
export function CustomersPage() {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const customers = useQuery({
    queryKey: queryKeys.customers(query),
    queryFn: () => listCustomers({ q: query || undefined }),
  });

  const rows = customers.data ?? [];

  return (
    <>
      <PageHeader
        title="Customers"
        description="Every account on this deployment. Open one to see their devices when they call."
      />

      <Card>
        <CardHeader
          title="Accounts"
          description={rows.length > 0 ? `${rows.length} shown.` : undefined}
          action={
            <div className="relative">
              <Search
                className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-ink-muted"
                aria-hidden
              />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Name or email"
                aria-label="Search customers"
                className="h-9 w-56 rounded-[--radius-control] border border-line bg-surface pl-8 pr-3 text-[13px] outline-none focus:border-accent focus:ring-2 focus:ring-accent-ring/40"
              />
            </div>
          }
        />

        {customers.isPending ? (
          <SkeletonRows rows={6} />
        ) : customers.isError ? (
          <ErrorState error={customers.error} onRetry={customers.refetch} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Users}
            title={query ? 'Nothing matches' : 'No accounts'}
            description={
              query ? 'No customer matches that name or email.' : 'Nobody has registered yet.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="border-b border-line text-left text-ink-muted">
                <tr>
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="hidden px-4 py-2 font-medium sm:table-cell">Email</th>
                  <th className="hidden px-4 py-2 font-medium lg:table-cell">Role</th>
                  <th className="px-4 py-2 text-right font-medium">Devices</th>
                  <th className="hidden px-4 py-2 text-right font-medium md:table-cell">Vehicles</th>
                  <th className="px-4 py-2 font-medium">Emergency contact</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {rows.map((customer) => (
                  <tr key={customer.id} className="transition-colors hover:bg-surface-sunken">
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/admin/customers/${customer.id}`}
                        className="font-medium text-accent hover:underline"
                      >
                        {customer.name}
                      </Link>
                    </td>
                    <td className="hidden px-4 py-2.5 text-ink-secondary sm:table-cell">
                      {customer.email}
                    </td>
                    <td className="hidden px-4 py-2.5 text-ink-secondary lg:table-cell">
                      {titleCase(customer.role)}
                    </td>
                    <td className="px-4 py-2.5 text-right" data-numeric>
                      {customer.device_count}
                    </td>
                    <td className="hidden px-4 py-2.5 text-right md:table-cell" data-numeric>
                      {customer.vehicle_count}
                    </td>
                    <td className="px-4 py-2.5">
                      {customer.has_emergency_contact ? (
                        <span className="text-ok">Set</span>
                      ) : (
                        // Not a styling choice: without one, a critical crash reaches
                        // only the owner, who may be unconscious.
                        <span className="text-warn">Not set</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}
